import {
  createApiRef,
  type DiscoveryApi,
  type FetchApi,
} from '@backstage/frontend-plugin-api';
import {
  loadCatalogConfig,
  loadCriteriaConfig,
  loadIndex,
  loadProduct,
  type OnMock,
} from './api';
import type {
  CatalogConfig,
  CriteriaConfig,
  MaturityIndex,
  ProductMaturity,
  ProductSummary,
} from './types';

/**
 * Reads Quality Maturity data from the quality-maturity backend's `/files`
 * proxy, falling back to bundled mock data (see api.ts).
 *
 * `baseUrl` stays an explicit argument: the page passes `''` once the backend
 * is known to be unavailable, so product loads take the zero-request mock path
 * (PAT-912).
 */
export interface QualityMaturityApi {
  getFilesBaseUrl(): Promise<string>;
  loadIndex(baseUrl: string, onMock?: OnMock): Promise<MaturityIndex>;
  loadCriteriaConfig(baseUrl: string, onMock?: OnMock): Promise<CriteriaConfig>;
  loadCatalogConfig(baseUrl: string, onMock?: OnMock): Promise<CatalogConfig>;
  loadProduct(
    baseUrl: string,
    summary: ProductSummary,
    config: CriteriaConfig,
    onMock?: OnMock,
  ): Promise<ProductMaturity>;
}

export const qualityMaturityApiRef = createApiRef<QualityMaturityApi>().with({
  id: 'plugin.quality-maturity.client',
  pluginId: 'quality-maturity',
});

export class QualityMaturityClient implements QualityMaturityApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  async getFilesBaseUrl(): Promise<string> {
    const baseUrl = await this.discoveryApi.getBaseUrl('quality-maturity');
    return `${baseUrl}/files`;
  }

  loadIndex(baseUrl: string, onMock?: OnMock): Promise<MaturityIndex> {
    return loadIndex(this.fetchApi, baseUrl, onMock);
  }

  loadCriteriaConfig(
    baseUrl: string,
    onMock?: OnMock,
  ): Promise<CriteriaConfig> {
    return loadCriteriaConfig(this.fetchApi, baseUrl, onMock);
  }

  loadCatalogConfig(baseUrl: string, onMock?: OnMock): Promise<CatalogConfig> {
    return loadCatalogConfig(this.fetchApi, baseUrl, onMock);
  }

  loadProduct(
    baseUrl: string,
    summary: ProductSummary,
    config: CriteriaConfig,
    onMock?: OnMock,
  ): Promise<ProductMaturity> {
    return loadProduct(this.fetchApi, baseUrl, summary, config, onMock);
  }
}
